import _ from 'lodash';
import Log from 'core/src/log';
import Icon from './icon';

const log = Log.instance("client/ui/container-menu");

/**
 * Trigger type fired by the configurable container menu, in both operating modes.
 * @type {string}
 */
export const ACTION_TRIGGER_TYPE = 'container-action';

/**
 * Icon used for the header button when the configuration supplies neither icon nor label.
 * @type {string}
 */
const DEFAULT_ICON = 'ellipsis-h';

/**
 * Normalises a single entry of $container.menu.options.
 * @param {*} option
 * @returns {object|undefined}    {action, label, tooltip, iconClass}, or undefined if unusable.
 */
function getOptionConfig(option) {
	if (!_.isObject(option) || !_.isString(option.action) || _.trim(option.action).length === 0) {
		log.warn("Ignoring invalid container menu option; expected an object with a non-empty `action`.", option);
		return undefined;
	}

	return {
		action: _.trim(option.action),
		label: _.isString(option.label) ? option.label : '',
		tooltip: _.isString(option.tooltip) ? option.tooltip : '',
		iconClass: Icon.getClass(option.icon)
	};
}

/**
 * Reads the optional menu configuration from container data ($container.menu).
 *
 * Two operating modes:
 * - `action` mode when `menu.action` is a non-empty string: a single button firing that action.
 * - `menu` mode when `menu.options` holds at least one usable entry: a button opening a
 *   floating menu, where each entry fires its own action.
 * `action` wins if both are configured.
 *
 * @param {object} containerData    Anything set on $container in the View.
 * @returns {object|undefined}      {mode, action, options, label, tooltip, iconClass}, or undefined if not configured.
 */
export function getMenuConfig(containerData) {
	const menu = _.get(containerData, 'menu');
	if (!_.isObject(menu)) {
		return undefined;
	}

	// The button face is shared by both modes. Without an icon or a label there would be
	// nothing to click, so fall back to a neutral overflow icon.
	const label = _.isString(menu.label) ? menu.label : '';
	const tooltip = _.isString(menu.tooltip) ? menu.tooltip : '';
	let iconClass = Icon.getClass(menu.icon);
	if (iconClass === '' && label === '') {
		iconClass = Icon.getClass(DEFAULT_ICON);
	}

	if (_.isString(menu.action) && _.trim(menu.action).length > 0) {
		return {
			mode: 'action',
			action: _.trim(menu.action),
			label,
			tooltip,
			iconClass
		};
	}

	if (_.isArray(menu.options)) {
		const options = _.map(menu.options, getOptionConfig).filter(option => !!option);
		if (options.length > 0) {
			return {
				mode: 'menu',
				options,
				label,
				tooltip,
				iconClass
			};
		}
	}

	return undefined;
}

export default { ACTION_TRIGGER_TYPE, getMenuConfig };
